import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Card,
  CardContent,
  CardActions,
  Stack } from
'@mui/material';
import { useIsMobile } from '../hooks/useResponsive';
import ResponsiveDialog from '../components/ResponsiveDialog';
import {
  Edit as EditIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  LocationOn as LocationIcon } from
'@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { PermissionGuard } from '../components/PermissionGuard';
import locationAccessService from '../services/locationAccessService';
import locationService from '../services/locationService';
import { useThemeSettings } from "../contexts/ThemeContext";
import { usersApi } from '../services/apiClient';
import { usePersistedFilter, FILTER_KEYS } from '../utils/filterPersistence';

const AVAILABLE_PERMISSIONS = {
  'login': 'Login to the system',
  'devices.view': 'View devices',
  'devices.create': 'Create devices',
  'devices.edit': 'Edit devices',
  'devices.delete': 'Delete devices',
  'locations.view': 'View locations',
  'locations.create': 'Create locations',
  'locations.edit': 'Edit locations',
  'locations.delete': 'Delete locations',
  'tickets.view': 'View tickets',
  'tickets.create': 'Create tickets',
  'tickets.edit': 'Edit tickets',
  'tickets.delete': 'Delete tickets',
  'camera_requests.view': 'View camera footage requests',
  'camera_requests.view_all': 'View all camera footage requests (admin/DPO)',
  'camera_requests.create': 'Create camera footage requests',
  'camera_requests.approve_site': 'Approve/reject as site manager',
  'camera_requests.approve_dpo': 'Approve/reject as DPO',
  'camera_requests.retrieve_footage': 'Retrieve and add footage (support team)',
  'field_interventions.view': 'View field interventions',
  'field_interventions.view_all': 'View all field interventions (admin)',
  'field_interventions.create': 'Create field interventions',
  'field_interventions.edit': 'Edit field interventions',
  'field_interventions.delete': 'Delete field interventions',
  'users.view': 'View users',
  'users.create': 'Create users',
  'users.edit': 'Edit users',
  'users.delete': 'Delete users',
  'users.permissions.manage': 'Manage user permissions',
  'company.view': 'View company information',
  'company.edit': 'Edit company settings',
  'company.settings.manage': 'Manage company-wide settings (retention policies, etc.)',
  'files.view': 'View files',
  'files.upload': 'Upload files',
  'files.download': 'Download files',
  'files.delete': 'Delete files',
  'files.share': 'Share files',
  'reports.view': 'View reports',
  'reports.create': 'Create reports',
  'reports.export': 'Export reports'
};

const DEFAULT_PERMISSIONS = [
'login',
'devices.view',
'locations.view',
'tickets.view',
'tickets.create'];


const UserManagement = () => {const { t } = useThemeSettings();
  const { user: currentUser } = useAuth();
  const { canManageUsers } = usePermissions();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState({
    default: [...DEFAULT_PERMISSIONS],
    custom: []
  });
  const [searchTerm, setSearchTerm] = usePersistedFilter(FILTER_KEYS.USER.SEARCH, '');

  // Location access state
  const [activeTab, setActiveTab] = useState(0);
  const [userLocationAccess, setUserLocationAccess] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('view');
  const [locationAccessLoading, setLocationAccessLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usersApi.getCompanyUsers();

      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditPermissions = async (userId) => {
    try {
      const response = await usersApi.getUserPermissions(userId);

      if (response.data.success) {
        setSelectedUser(response.data.user);
        setSelectedPermissions(response.data.permissionsConfig || {
          default: [...DEFAULT_PERMISSIONS],
          custom: []
        });

        // Load location access and available locations
        await loadLocationAccessData(userId);

        setEditDialogOpen(true);
      }
    } catch (err) {
      console.error('Error fetching user permissions:', err);
      setError('Failed to load user permissions. ' + (err.response?.data?.error || err.message));
    }
  };

  const loadLocationAccessData = async (userId) => {
    setLocationAccessLoading(true);
    try {
      // Load user's current location access
      const accessData = await locationAccessService.getUserLocationAccess(userId);
      setUserLocationAccess(accessData);

      // Load available sites (root locations) for granting access
      // Use the dedicated getUserSites endpoint which returns only Sites
      // Then get all locations to filter root sites from them for the admin
      const locations = await locationService.getLocations();
      console.log('[UserManagement] All locations for site selection:', locations.length);

      // Filter to only show Sites (root locations with no parent) as access is managed at Site level
      const siteLocations = locations.filter((loc) => {
        // A site is a root location - has no parent
        // Handle various ways parentLocation might be stored: null, 0, '', '0', 'null', undefined
        const parentLoc = loc.parentLocation ?? loc.parent_id ?? loc.parentId ?? loc.parent;
        const isRootLocation = !parentLoc || parentLoc === 0 || parentLoc === '' || parentLoc === '0' || parentLoc === 'null';
        // Also check locationType - type 1 is typically 'Site'
        const isSiteType = loc.locationType === 1 || loc.locationTypeId === 1 || loc.locationTypeModel?.id === 1;
        // Return true if it's a root location OR if it's explicitly marked as a Site type
        return isRootLocation || isSiteType;
      });

      console.log('[UserManagement] Filtered site locations:', siteLocations.length);
      setAvailableLocations(siteLocations);
    } catch (err) {
      console.error('Error loading location access data:', err);
      setError('Failed to load location access data. ' + err.message);
    } finally {
      setLocationAccessLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      const response = await usersApi.updateUserPermissions(selectedUser.id, {
        permissions: selectedPermissions
      });

      if (response.data.success) {
        setSuccess(`Permissions updated successfully for ${selectedUser.username}`);
        setEditDialogOpen(false);
        fetchUsers(); // Refresh the list
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError('Failed to update permissions. ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePermissionToggle = (permission) => {
    setSelectedPermissions((prev) => {
      const custom = prev.custom || [];
      const isSelected = custom.includes(permission);

      return {
        ...prev,
        custom: isSelected ?
        custom.filter((p) => p !== permission) :
        [...custom, permission]
      };
    });
  };

  const isPermissionSelected = (permission) => {
    const allPerms = [
    ...(selectedPermissions.default || []),
    ...(selectedPermissions.custom || [])];

    return allPerms.includes(permission);
  };

  const handleGrantLocationAccess = async () => {
    if (!selectedUser || !selectedLocation) return;

    try {
      await locationAccessService.grantAccess({
        userId: selectedUser.id,
        locationId: selectedLocation.id,
        accessLevel: selectedAccessLevel
      });

      setSuccess(`Location access granted successfully for ${selectedUser.username}`);

      // Reload location access data
      await loadLocationAccessData(selectedUser.id);

      // Reset selection
      setSelectedLocation(null);
      setSelectedAccessLevel('view');

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error granting location access:', err);
      setError('Failed to grant location access. ' + err.message);
    }
  };

  const handleRevokeLocationAccess = async (accessId) => {
    if (!selectedUser) return;

    try {
      await locationAccessService.revokeAccess(accessId);

      setSuccess(`Location access revoked successfully`);

      // Reload location access data
      await loadLocationAccessData(selectedUser.id);

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error revoking location access:', err);
      setError('Failed to revoke location access. ' + err.message);
    }
  };

  const handleUpdateLocationAccess = async (accessId, newAccessLevel) => {
    if (!selectedUser) return;

    try {
      await locationAccessService.updateAccess(accessId, {
        accessLevel: newAccessLevel
      });

      setSuccess(`Location access updated successfully`);

      // Reload location access data
      await loadLocationAccessData(selectedUser.id);

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error updating location access:', err);
      setError('Failed to update location access. ' + err.message);
    }
  };

  const filteredUsers = users.filter((user) =>
  user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
  user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPermissionsByCategory = () => {
    const categories = {};
    Object.entries(AVAILABLE_PERMISSIONS).forEach(([perm, desc]) => {
      const category = perm.split('.')[0];
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push({ permission: perm, description: desc });
    });
    return categories;
  };

  const renderMobileView = () =>
  <Box>
      {filteredUsers.map((user) =>
    <Card key={user.id} className="card-animated card-enter" sx={{ mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("SSHDialog.username") : "Username"}

            </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {user.username}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("UserManagement.email") : "Email"}

            </Typography>
                <Typography variant="body2">
                  {user.email}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("UserManagement.role") : "Role"}

            </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                label={user.role}
                color={user.role === 'admin' ? 'error' : 'default'}
                size="small" />
              
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("UserManagement.permissions") : "Permissions"}

            </Typography>
                <Typography variant="body2">
                  {Array.isArray(user.permissions) ? user.permissions.length : 0}{t ? t("UserManagement.permissions_1") : "permissions"}
            </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t ? t("DeviceTable.status") : "Status"}

            </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                label={user.active ? 'Active' : 'Inactive'}
                color={user.active ? 'success' : 'default'}
                size="small" />
              
                </Box>
              </Box>
            </Stack>
          </CardContent>
          <Divider />
          <CardActions>
            <Button
          startIcon={<EditIcon />}
          onClick={() => handleEditPermissions(user.id)}
          disabled={user.role === 'admin' && currentUser.role !== 'admin'}
          color="primary"
          size="small">{t ? t("UserManagement.edit_permissions") : "Edit Permissions"}


        </Button>
          </CardActions>
        </Card>
    )}
      {filteredUsers.length === 0 &&
    <Paper sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">{t ? t("UserManagement.no_users_found") : "No users found"}

      </Typography>
        </Paper>
    }
    </Box>;


  if (!canManageUsers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t ? t("UserManagement.you_dont_have_permission_manage_user") : "You don't have permission to manage user permissions."}

        </Alert>
      </Box>);

  }

  return (
    <Box
      className="page-transition-enter"
      sx={{
        p: 3,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" className="slide-down">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{t ? t("UserManagement.user_permissions_management") : "User Permissions Management"}

        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={loading}>{t ? t("DeviceStatusDashboard.refresh") : "Refresh"}


        </Button>
      </Box>

      {error &&
      <Alert severity="error" className="slide-up" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      }

      {success &&
      <Alert severity="success" className="slide-up" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      }

      <Paper className="card-animated" sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label={t ? t("UserManagement.search_users") : "Search Users"}
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t ? t("UserManagement.search_username_email") : "Search by username or email..."} />
        
      </Paper>

      {loading ?
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <div className="spinner" />
        </Box> :
      isMobile ?
      renderMobileView() :

      <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t ? t("SSHDialog.username") : "Username"}</TableCell>
                <TableCell>{t ? t("UserManagement.email") : "Email"}</TableCell>
                <TableCell>{t ? t("UserManagement.role") : "Role"}</TableCell>
                <TableCell>{t ? t("UserManagement.permissions_count") : "Permissions Count"}</TableCell>
                <TableCell>{t ? t("DeviceTable.status") : "Status"}</TableCell>
                <TableCell align="right">{t ? t("DeviceTable.actions") : "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) =>
            <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                  label={user.role}
                  color={user.role === 'admin' ? 'error' : 'default'}
                  size="small" />
                
                  </TableCell>
                  <TableCell>
                    {Array.isArray(user.permissions) ? user.permissions.length : 0}{t ? t("UserManagement.permissions_1") : "permissions"}
              </TableCell>
                  <TableCell>
                    <Chip
                  label={user.active ? 'Active' : 'Inactive'}
                  color={user.active ? 'success' : 'default'}
                  size="small" />
                
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t ? t("UserManagement.edit_permissions") : "Edit Permissions"}>
                      <IconButton
                    onClick={() => handleEditPermissions(user.id)}
                    disabled={user.role === 'admin' && currentUser.role !== 'admin'}
                    color="primary">
                    
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
            )}
              {filteredUsers.length === 0 &&
            <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">{t ? t("UserManagement.no_users_found") : "No users found"}

                </Typography>
                  </TableCell>
                </TableRow>
            }
            </TableBody>
          </Table>
        </TableContainer>
      }

      {/* Edit Permissions Dialog */}
      <ResponsiveDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setActiveTab(0);
        }}
        maxWidth="md"
        fullWidth>
        
        <DialogTitle>{t ? t("UserManagement.edit_permissions_1") : "Edit Permissions:"}
          {selectedUser?.username}
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab icon={<SecurityIcon />} label={t ? t("UserManagement.system_permissions") : "System Permissions"} />
            <Tab icon={<LocationIcon />} label={t ? t("UserManagement.location_access") : "Location Access"} />
          </Tabs>

          {/* System Permissions Tab */}
          {activeTab === 0 &&
          <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">{t ? t("UserManagement.manage_custom_permissions_this_user") : "Manage custom permissions for this user. Default permissions are already enabled."}

              </Typography>
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t ? t("UserManagement.default_permissions_always_enabled") : "Default Permissions (Always Enabled):"}

            </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedPermissions.default?.map((perm) =>
              <Chip
                key={perm}
                label={perm}
                size="small"
                sx={{ mr: 0.5, mb: 0.5 }}
                color="primary"
                variant="outlined" />

              )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 2 }}>{t ? t("UserManagement.additional_permissions") : "Additional Permissions:"}

            </Typography>

              {Object.entries(getPermissionsByCategory()).map(([category, perms]) =>
            <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, textTransform: 'capitalize' }}>
                    {category}
                  </Typography>
                  <FormGroup>
                    {perms.map(({ permission, description }) => {
                  const isDefault = selectedPermissions.default?.includes(permission);
                  const isSelected = isPermissionSelected(permission);

                  return (
                    <FormControlLabel
                      key={permission}
                      control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => !isDefault && handlePermissionToggle(permission)}
                        disabled={isDefault} />

                      }
                      label={
                      <Box>
                              <Typography variant="body2">{permission}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {description}
                              </Typography>
                            </Box>
                      } />);


                })}
                  </FormGroup>
                </Box>
            )}
            </Box>
          }

          {/* Location Access Tab */}
          {activeTab === 1 &&
          <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Manage which Sites this user can access. Access is granted at the Site level only -
                  granting access to a Site automatically grants access to all its child locations.
                  Users can only access locations from their own company.
                </Typography>
              </Alert>

              {/* Add Location Access Section */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>{t ? t("UserManagement.grant_new_site_access") : "Grant New Site Access:"}

              </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Autocomplete
                  options={availableLocations.filter(
                    (loc) => !userLocationAccess.some((access) => access.location?.id === loc.id)
                  )}
                  getOptionLabel={(option) => option.friendlyName || option.location_UUID || 'Unnamed Site'}
                  value={selectedLocation}
                  onChange={(event, newValue) => setSelectedLocation(newValue)}
                  renderInput={(params) =>
                  <TextField {...params} label={t ? t("UserManagement.select_site") : "Select Site"} size="small" />
                  }
                  sx={{ flex: 1 }}
                  disabled={locationAccessLoading} />
                
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{t ? t("UserManagement.access_level") : "Access Level"}</InputLabel>
                    <Select
                    value={selectedAccessLevel}
                    onChange={(e) => setSelectedAccessLevel(e.target.value)}
                    label={t ? t("UserManagement.access_level") : "Access Level"}
                    disabled={locationAccessLoading}>
                    
                      <MenuItem value="view">{t ? t("TicketsTable.view") : "View"}</MenuItem>
                      <MenuItem value="edit">{t ? t("DeviceTable.edit") : "Edit"}</MenuItem>
                      <MenuItem value="admin">{t ? t("UserManagement.admin") : "Admin"}</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleGrantLocationAccess}
                  disabled={!selectedLocation || locationAccessLoading}>{t ? t("UserManagement.grant") : "Grant"}


                </Button>
                </Box>
              </Paper>

              {/* Current Location Access List */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t ? t("UserManagement.current_site_access") : "Current Site Access ("}
              {userLocationAccess.length}):
              </Typography>

              {locationAccessLoading ?
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box> :
            userLocationAccess.length === 0 ?
            <Alert severity="warning" sx={{ mt: 2 }}>{t ? t("UserManagement.no_site_access_granted_yet_grant_access") : "No site access granted yet. Grant access to a Site to allow this user to view it and all its child locations."}

            </Alert> :

            <List sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {userLocationAccess.map((access) =>
              <ListItem key={access.id} divider>
                      <ListItemText
                  primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon fontSize="small" color="primary" />
                            <Typography variant="body1">
                              {access.location?.friendlyName || access.location?.location_UUID || 'Unknown Site'}
                            </Typography>
                          </Box>
                  }
                  secondary={
                  <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">{t ? t("UserManagement.granted") : "Granted:"}
                      {new Date(access.grantedAt).toLocaleDateString()}
                              {access.grantor && ` by ${access.grantor.username}`}
                            </Typography>
                          </Box>
                  } />
                
                      <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <Select
                      value={access.accessLevel}
                      onChange={(e) => handleUpdateLocationAccess(access.id, e.target.value)}
                      size="small">
                      
                            <MenuItem value="view">{t ? t("TicketsTable.view") : "View"}</MenuItem>
                            <MenuItem value="edit">{t ? t("DeviceTable.edit") : "Edit"}</MenuItem>
                            <MenuItem value="admin">{t ? t("UserManagement.admin") : "Admin"}</MenuItem>
                          </Select>
                        </FormControl>
                        <Tooltip title={t ? t("UserManagement.revoke_access") : "Revoke Access"}>
                          <IconButton
                      edge="end"
                      onClick={() => handleRevokeLocationAccess(access.id)}
                      color="error"
                      size="small">
                      
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
              )}
                </List>
            }
            </Box>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setActiveTab(0);
          }}>{t ? t("LocationDialog.cancel") : "Cancel"}

          </Button>
          {activeTab === 0 &&
          <Button onClick={handleSavePermissions} variant="contained" color="primary">{t ? t("UserManagement.save_permissions") : "Save Permissions"}

          </Button>
          }
          {activeTab === 1 &&
          <Button onClick={() => {
            setEditDialogOpen(false);
            setActiveTab(0);
          }} variant="contained" color="primary">{t ? t("UserManagement.done") : "Done"}

          </Button>
          }
        </DialogActions>
      </ResponsiveDialog>
    </Box>);

};

export default UserManagement;